import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogOut, ArrowLeft, Plus, Pencil, Trash2, Loader2, Users as UsersIcon, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSION_LABELS = {
  view_simulator: { label: "Acede ao Simulador", help: "Permite abrir e usar a página do simulador." },
  view_admin: { label: "Acede ao Backoffice", help: "Permite entrar nas páginas de administração (tabelas IRS, custos, etc.)." },
  view_net_salary: { label: "Vê Salário Líquido", help: "Mostra o widget de salário líquido do colaborador no simulador." },
  view_company_cost: { label: "Vê Custo da Empresa", help: "Mostra o widget de custo total para a empresa." },
  view_margin: { label: "Vê Margem", help: "Mostra a margem (%) e o respetivo badge inline. Se desativado, oculta valor da margem." },
  view_business_decision: { label: "Vê Decisão de Negócio", help: "Widget de score e recomendação automática (Aprovado / Necessita Revisão / Não Recomendado)." },
  view_premium: { label: "Vê Prémios calculados", help: "Mostra os prémios calculados (por margem, fixos, fee perm)." },
  view_annual_view: { label: "Vê Vista Anual", help: "Permite aceder ao separador 'Anual' com a vista mês a mês." },
  view_proposals: { label: "Cria/Vê Propostas", help: "Permite gerar e visualizar propostas comerciais em PDF." },
  view_candidates: { label: "Vê Candidatos", help: "Permite aceder à página de candidatos e links de partilha." },
  view_history: { label: "Vê Histórico", help: "Permite ver a tab Histórico no simulador (rates, margens, clientes) com filtros." },
  edit_simulator_overrides: {
    label: "Pode editar overrides",
    help: "Permite alterar manualmente valores padrão dos benefícios no simulador (ex.: subsídio refeição, seguro de saúde, IHT). Se desativado, esses inputs ficam bloqueados.",
  },
  export_pdf: { label: "Pode exportar PDFs", help: "Permite gerar/descarregar PDFs (proposta financeira e propostas assinadas)." },
};

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);

  // User dialog
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ email: "", name: "", password: "", role: "user", group_id: "", phone: "", job_function: "" });

  // Group dialog
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ key: "", name: "", description: "", permissions: {}, active: true });

  const loadAll = useCallback(async () => {
    try {
      const [u, g] = await Promise.all([
        axios.get(`${API}/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/user-groups`, { headers: getAuthHeaders() }),
      ]);
      setUsers(u.data);
      setGroups(g.data);
    } catch (e) {
      toast.error("Erro a carregar");
    } finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ---------------- USERS ----------------
  const openNewUser = () => { setEditingUser(null); setUserForm({ email: "", name: "", password: "", role: "user", group_id: groups[0]?.id || "", phone: "", job_function: "" }); setUserDialogOpen(true); };
  const openEditUser = (u) => {
    setEditingUser(u);
    setUserForm({ email: u.email, name: u.name, password: "", role: u.role, group_id: u.group_id || "", phone: u.phone || "", job_function: u.job_function || "" });
    setUserDialogOpen(true);
  };
  const toggleAdmin = async (u) => {
    // Atalho: promove/rebaixa role sem abrir o dialog. Confirma antes.
    const promoting = u.role !== "admin";
    const msg = promoting
      ? `Promover ${u.email} a ADMIN? Vai ganhar acesso total ao backoffice, prémios e custos da empresa.`
      : `Rebaixar ${u.email} de admin para user comum?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.put(`${API}/users/${u.id}`, { role: promoting ? "admin" : "user" }, { headers: getAuthHeaders() });
      toast.success(promoting ? `${u.email} promovido a admin` : `${u.email} rebaixado a user`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao alterar role");
    }
  };

  const submitUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const payload = { name: userForm.name, role: userForm.role, group_id: userForm.group_id };
        // Só envia password se admin escreveu algo (permite manter a password anterior)
        if (userForm.password && userForm.password.trim().length > 0) {
          payload.password = userForm.password;
        }
        await axios.put(`${API}/users/${editingUser.id}`, payload, { headers: getAuthHeaders() });
        toast.success(payload.password ? "Utilizador atualizado (password redefinida)" : "Utilizador atualizado");
      } else {
        if (!userForm.password) { toast.error("Password obrigatória"); return; }
        await axios.post(`${API}/users`, userForm, { headers: getAuthHeaders() });
        toast.success("Utilizador criado");
      }
      setUserDialogOpen(false);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const deleteUser = async (id) => {
    if (!window.confirm("Eliminar utilizador?")) return;
    await axios.delete(`${API}/users/${id}`, { headers: getAuthHeaders() });
    loadAll();
  };

  // ---------------- GROUPS ----------------
  const openNewGroup = () => { setEditingGroup(null); setGroupForm({ key: "", name: "", description: "", permissions: {}, active: true }); setGroupDialogOpen(true); };
  const openEditGroup = (g) => {
    setEditingGroup(g);
    setGroupForm({ key: g.key, name: g.name, description: g.description || "", permissions: { ...(g.permissions || {}) }, active: g.active });
    setGroupDialogOpen(true);
  };
  const submitGroup = async (e) => {
    e.preventDefault();
    try {
      if (editingGroup) {
        await axios.put(`${API}/user-groups/${editingGroup.id}`, { name: groupForm.name, description: groupForm.description, permissions: groupForm.permissions, active: groupForm.active }, { headers: getAuthHeaders() });
        toast.success("Grupo atualizado");
      } else {
        await axios.post(`${API}/user-groups`, groupForm, { headers: getAuthHeaders() });
        toast.success("Grupo criado");
      }
      setGroupDialogOpen(false);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const deleteGroup = async (g) => {
    if (g.is_system) { toast.error("Grupos do sistema não podem ser eliminados"); return; }
    if (!window.confirm(`Eliminar grupo "${g.name}"?`)) return;
    await axios.delete(`${API}/user-groups/${g.id}`, { headers: getAuthHeaders() });
    loadAll();
  };

  const togglePerm = (key) => setGroupForm({ ...groupForm, permissions: { ...groupForm.permissions, [key]: !groupForm.permissions[key] } });

  return (
    <div className="min-h-screen bg-background" data-testid="admin-users-page">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg px-3 py-1.5">
              <img src="/evolutt_logo_white.png" alt="Evolutt" className="h-8" />
            </div>
            <p className="text-xs text-muted-foreground">Utilizadores & Permissões</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="w-4 h-4 mr-2" />Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin"><Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button></Link>
          <div>
            <h1 className="font-manrope font-extrabold text-2xl">Utilizadores & Grupos</h1>
            <p className="text-muted-foreground">Gestão de acessos e permissões granulares por widget</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="users" data-testid="tab-users"><UsersIcon className="w-4 h-4 mr-2" />Utilizadores ({users.length})</TabsTrigger>
              <TabsTrigger value="groups" data-testid="tab-groups"><ShieldCheck className="w-4 h-4 mr-2" />Grupos ({groups.length})</TabsTrigger>
            </TabsList>

            {/* USERS TAB */}
            <TabsContent value="users" className="mt-6">
              <div className="flex justify-end mb-4">
                <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-brand-600 text-white hover:bg-brand-700 font-bold" onClick={openNewUser}>
                      <Plus className="w-4 h-4 mr-2" />Novo Utilizador
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingUser ? "Editar" : "Novo"} Utilizador</DialogTitle>
                      <DialogDescription>
                        Preencha os dados do utilizador, escolha o papel (Admin/User) e atribua um Grupo para definir permissões.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitUser} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} disabled={!!editingUser} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Nome</Label>
                        <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Telemóvel</Label>
                        <Input
                          type="tel"
                          placeholder="+351 9XX XXX XXX"
                          value={userForm.phone}
                          onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                          data-testid="user-phone-input"
                        />
                        <p className="text-[10px] text-muted-foreground">Aparece no slide final do PDF como contacto direto.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Função</Label>
                        <Input
                          placeholder="Ex.: Recruiter Senior, Manager, ..."
                          value={userForm.job_function}
                          onChange={(e) => setUserForm({ ...userForm, job_function: e.target.value })}
                          data-testid="user-job-function-input"
                        />
                        <p className="text-[10px] text-muted-foreground">Aparece no slide final do PDF junto aos contactos.</p>
                      </div>
                      {!editingUser ? (
                        <div className="space-y-2">
                          <Label>Password</Label>
                          <Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label>Nova Password <span className="text-[10px] text-muted-foreground font-normal">(opcional — deixe vazio para manter)</span></Label>
                          <Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} placeholder="Deixar vazio = não alterar" data-testid="user-password-reset-input" />
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Role</Label>
                          <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="user">User</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Grupo</Label>
                          <Select value={userForm.group_id} onValueChange={(v) => setUserForm({ ...userForm, group_id: v })}>
                            <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                            <SelectContent>
                              {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>Cancelar</Button>
                        <Button type="submit" className="bg-brand-600 text-white hover:bg-brand-700">{editingUser ? "Guardar" : "Criar"}</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Grupo</TableHead><TableHead className="text-right">Ações</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {users.map(u => {
                        const grp = groups.find(g => g.id === u.group_id);
                        return (
                          <TableRow key={u.id}>
                            <TableCell className="font-medium">{u.name}</TableCell>
                            <TableCell>{u.email}</TableCell>
                            <TableCell><Badge variant={u.role === "admin" ? "default" : "secondary"}>{u.role}</Badge></TableCell>
                            <TableCell>{grp ? <Badge variant="outline">{grp.name}</Badge> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                title={u.role === "admin" ? "Rebaixar a user" : "Promover a Admin"}
                                onClick={() => toggleAdmin(u)}
                                data-testid={`toggle-admin-btn-${u.id}`}
                                className={u.role === "admin" ? "text-brand-600" : ""}
                              >
                                <ShieldCheck className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openEditUser(u)}><Pencil className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => deleteUser(u.id)}><Trash2 className="w-4 h-4" /></Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* GROUPS TAB */}
            <TabsContent value="groups" className="mt-6">
              <div className="flex justify-end mb-4">
                <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-brand-600 text-white hover:bg-brand-700 font-bold" onClick={openNewGroup}>
                      <Plus className="w-4 h-4 mr-2" />Novo Grupo
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingGroup ? "Editar" : "Novo"} Grupo</DialogTitle>
                      <DialogDescription>
                        Defina o nome, descrição e as permissões granulares por widget (cada permissão tem uma descrição que explica o impacto).
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitGroup} className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Chave (identificador interno)</Label>
                          <Input value={groupForm.key} onChange={(e) => setGroupForm({ ...groupForm, key: e.target.value })} disabled={!!editingGroup} placeholder="ex.: senior_recruiter" required />
                        </div>
                        <div className="space-y-2">
                          <Label>Nome a apresentar</Label>
                          <Input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} required />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Descrição</Label>
                        <Input value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-base font-semibold">Permissões granulares por widget</Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 p-3 bg-slate-50 rounded">
                          {Object.entries(PERMISSION_LABELS).map(([key, def]) => (
                            <label key={key} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-white p-1 rounded" title={def.help}>
                              <Switch checked={!!groupForm.permissions[key]} onCheckedChange={() => togglePerm(key)} />
                              <div className="flex-1">
                                <div>{def.label}</div>
                                <p className="text-[10px] text-muted-foreground leading-tight">{def.help}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Ativo</Label>
                        <Switch checked={groupForm.active} onCheckedChange={(c) => setGroupForm({ ...groupForm, active: c })} />
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setGroupDialogOpen(false)}>Cancelar</Button>
                        <Button type="submit" className="bg-brand-600 text-white hover:bg-brand-700">{editingGroup ? "Guardar" : "Criar"}</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Chave</TableHead><TableHead>Permissões ativas</TableHead><TableHead>Sistema</TableHead><TableHead className="text-right">Ações</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {groups.map(g => {
                        const activeCount = Object.values(g.permissions || {}).filter(Boolean).length;
                        return (
                          <TableRow key={g.id}>
                            <TableCell className="font-medium">{g.name}<p className="text-xs text-muted-foreground">{g.description}</p></TableCell>
                            <TableCell><code className="text-xs">{g.key}</code></TableCell>
                            <TableCell><Badge variant="secondary">{activeCount} / {Object.keys(PERMISSION_LABELS).length}</Badge></TableCell>
                            <TableCell>{g.is_system && <Badge variant="outline" className="text-amber-700 border-amber-300">Sistema</Badge>}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="icon" onClick={() => openEditGroup(g)}><Pencil className="w-4 h-4" /></Button>
                              {!g.is_system && (
                                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => deleteGroup(g)}><Trash2 className="w-4 h-4" /></Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
};

export default AdminUsers;
